import React, { useState, useEffect, useMemo } from "react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import { X, Minus, Plus, Save, Loader2, Package, Check } from "lucide-react";
import { getImageUrl } from "../../utils/getImageUrl";

// Cambia un ítem puntual de la cuenta de una mesa por otro producto, o solo ajusta su
// cantidad — se preselecciona el producto que ya tenía para que "solo cambiar la
// cantidad" sea un caso trivial (no hace falta re-elegir el mismo producto).
export default function ItemSwapModal({ companyId, item, onSave, onCancel, saving }) {
  const [secciones, setSecciones] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(item.productId ?? null);
  const [qty, setQty] = useState(item.quantity ?? 1);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const [resSec, resProd] = await Promise.allSettled([
          API.get(`/secciones/company/${companyId}`),
          API.get(`/products/company/${companyId}`),
        ]);
        const secs = resSec.status === "fulfilled" ? (resSec.value.data?.data?.secciones ?? resSec.value.data?.data ?? []) : [];
        setSecciones(Array.isArray(secs) ? secs : []);
        const prods = resProd.status === "fulfilled" ? (resProd.value.data?.data?.products ?? resProd.value.data?.data ?? []) : [];
        setProducts((Array.isArray(prods) ? prods : []).filter((p) => p.visible !== false));
      } catch {
        toast.error("Error al cargar productos");
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId]);

  const grouped = useMemo(() => {
    const map = {};
    const sinSeccion = [];
    products.forEach((p) => {
      const sid = p.seccionId ?? p.seccion?.id ?? null;
      if (sid) { if (!map[sid]) map[sid] = []; map[sid].push(p); }
      else sinSeccion.push(p);
    });
    const sections = secciones.filter((s) => map[s.id]?.length).map((s) => ({ id: s.id, name: s.name, products: map[s.id] }));
    if (sinSeccion.length) sections.push({ id: "sin-seccion", name: "Otros", products: sinSeccion });
    return sections;
  }, [products, secciones]);

  const selectedProduct = products.find((p) => p.id === selectedId);

  const handleSave = () => {
    if (!selectedId) { toast.warn("Elige un producto"); return; }
    onSave(selectedId, qty);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-gray-800">Cambiar ítem</h3>
            <p className="text-xs text-gray-400">Antes: {item.quantity}x {item.title}</p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>

        <div className="px-5 pt-4 flex items-center justify-between gap-3 border-b border-gray-50 pb-4">
          <span className="text-sm font-semibold text-gray-600">Cantidad</span>
          <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-1">
            <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-800">
              <Minus size={14} />
            </button>
            <span className="w-6 text-center text-sm font-bold text-gray-800">{qty}</span>
            <button type="button" onClick={() => setQty((q) => q + 1)} className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-800">
              <Plus size={14} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 size={24} className="animate-spin text-supernova-500" /></div>
          ) : grouped.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-gray-300">
              <Package size={36} className="mb-2 opacity-40" />
              <p className="text-sm text-gray-400">No hay productos disponibles</p>
            </div>
          ) : (
            grouped.map((sec) => (
              <section key={sec.id}>
                <h4 className="font-bold text-gray-700 text-xs uppercase tracking-wide mb-2">{sec.name}</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {sec.products.map((p) => {
                    const isSelected = selectedId === p.id;
                    const hasOffer = p.offerPrice && Number(p.offerPrice) < Number(p.price);
                    const displayPrice = hasOffer ? Number(p.offerPrice) : Number(p.price);
                    return (
                      <button
                        type="button"
                        key={p.id}
                        onClick={() => setSelectedId(p.id)}
                        className={`relative text-left bg-white rounded-2xl border-2 overflow-hidden transition ${
                          isSelected ? "border-supernova-500 ring-2 ring-supernova-100" : "border-gray-100 hover:border-gray-200"
                        }`}
                      >
                        {isSelected && (
                          <span className="absolute top-1.5 right-1.5 z-10 w-5 h-5 rounded-full bg-supernova-500 text-white flex items-center justify-center">
                            <Check size={12} />
                          </span>
                        )}
                        <div className="h-20 bg-gray-50">
                          {p.images?.[0] ? (
                            <img src={getImageUrl(p.images[0])} alt={p.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center"><Package size={22} className="text-gray-200" /></div>
                          )}
                        </div>
                        <div className="p-2">
                          <p className="font-bold text-xs text-gray-800 leading-tight line-clamp-2">{p.title}</p>
                          <span className="text-xs font-black text-supernova-600">${displayPrice.toLocaleString("es-CO")}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))
          )}
        </div>

        <div className="flex items-center gap-3 px-5 py-4 border-t border-gray-100">
          <div className="flex-1 text-sm">
            {selectedProduct ? (
              <p className="text-gray-600">
                Queda: <span className="font-bold text-gray-800">{qty}x {selectedProduct.title}</span>
              </p>
            ) : (
              <p className="text-gray-400">Elige un producto</p>
            )}
          </div>
          <button onClick={onCancel} className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-semibold transition">
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={!selectedId || saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-supernova-500 hover:bg-supernova-600 text-white text-sm font-bold transition disabled:opacity-50"
          >
            <Save size={14} /> {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}
